import { z } from 'zod/v4';
import { type SolarPlacementRules } from './solarPlacementRules';
import { hasMeasuredPlane, type SolarPlaneFit } from './solarPlaneFit';

export const solarRoofPlaneSchema = z.object({
  pitchDegrees: z.number().min(0).max(90),
  azimuthDegrees: z.number().min(0).max(360),
});
export const solarMountingSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('auto') }),
  z.object({ mode: z.literal('flush') }),
  z.object({
    mode: z.literal('tilt'),
    /** Absolute panel angle above horizontal, not an addition to roof pitch. */
    panelTiltDegrees: z.number().min(0).max(45),
  }),
]);
export type SolarRoofPlane = z.infer<typeof solarRoofPlaneSchema>;
export type SolarMounting = z.infer<typeof solarMountingSchema>;

/** A row-axis metre in plan projected onto the known physical roof surface. */
export const roofDistancePerGroundRowM = (
  roof: SolarRoofPlane,
  rowAzimuthDegrees: number
): number =>
  Math.hypot(
    1,
    Math.tan((roof.pitchDegrees * Math.PI) / 180) *
      Math.cos(((rowAzimuthDegrees - roof.azimuthDegrees) * Math.PI) / 180)
  );

/** Only an unedited analysis documents applied angles as the roof plane. */
export const recordedRoofPlane = (
  face: SolarRoofPlane & {
    roofPlane?: SolarRoofPlane;
    planeFit?: SolarPlaneFit;
    mount?: { roofPitchDegrees: number; roofAzimuthDegrees: number };
  },
  edited?: boolean
): SolarRoofPlane | undefined => {
  if (!hasMeasuredPlane(face)) return undefined;
  if (face.roofPlane) return face.roofPlane;
  if (face.mount)
    return {
      pitchDegrees: face.mount.roofPitchDegrees,
      azimuthDegrees: face.mount.roofAzimuthDegrees,
    };
  if (edited) return undefined;
  return {
    pitchDegrees: face.pitchDegrees,
    azimuthDegrees: face.azimuthDegrees,
  };
};

/** Legacy fields describe the applied panel plane; never infer a lost roof plane. */
export const physicalRoofPlane = (
  face: SolarRoofPlane & {
    roofPlane?: SolarRoofPlane;
  }
): SolarRoofPlane => face.roofPlane ?? face;

export const resolveSolarMounting = (
  roof: SolarRoofPlane,
  mounting: SolarMounting | undefined,
  rules: SolarPlacementRules,
  arrayOffsetDegrees = 0,
  arrayAzimuthDegrees = roof.azimuthDegrees
): SolarRoofPlane & { onFrames: boolean } => {
  const onFrames =
    mounting?.mode === 'tilt' ||
    (mounting?.mode !== 'flush' &&
      roof.pitchDegrees <= rules.flatRoof.maxSlopeDeg);
  const frameTilt =
    mounting?.mode === 'tilt'
      ? mounting.panelTiltDegrees
      : rules.flatRoof.frameTiltDeg;
  return {
    onFrames,
    pitchDegrees: onFrames ? frameTilt : roof.pitchDegrees,
    azimuthDegrees:
      (((onFrames
        ? arrayAzimuthDegrees + arrayOffsetDegrees
        : roof.azimuthDegrees) %
        360) +
        360) %
      360,
  };
};
